import { useState, useMemo, useEffect } from "react";
import locationsByProvince from "../data/locationsData";
import { adaptDirectory, flattenStatic } from "../data/adaptDirectory";

const STATIC_FALLBACK = flattenStatic(locationsByProvince);

const OfficeLocationsCentersSection = () => {
  const [selectedFilter, setSelectedFilter] = useState("Todas las provincias");
  const [itemsToShow, setItemsToShow] = useState(9);
  const [allLocations, setAllLocations] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // cache: 'no-store' fuerza al navegador a saltarse la disk cache y revalidar
    // contra el edge en cada carga. El edge sigue cacheando 5 min (s-maxage=300
    // en functions/api/directory.js) así que el coste es despreciable. Sin esto,
    // Cloudflare reescribe nuestro max-age=300 a max-age=2678400 (31 días) en la
    // respuesta al browser, y los usuarios se comen datos viejos durante un mes.
    fetch('/api/directory', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (!cancelled) setAllLocations(adaptDirectory(data));
      })
      .catch((err) => {
        console.error('Directory fetch failed, using static fallback:', err);
        if (!cancelled) setAllLocations(STATIC_FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isLoading = allLocations === null;
  const locationsForRender = useMemo(() => allLocations ?? [], [allLocations]);

  const provinces = useMemo(() => {
    return ["Todas las provincias", ...new Set(locationsForRender.map((l) => l.province))];
  }, [locationsForRender]);

  const filteredLocations = useMemo(() => {
    if (selectedFilter === "Todas las provincias") return locationsForRender;
    return locationsForRender.filter((loc) => loc.province === selectedFilter);
  }, [locationsForRender, selectedFilter]);

  // Paginated locations
  const displayedLocations = filteredLocations.slice(0, itemsToShow);
  const hasMore = itemsToShow < filteredLocations.length;

  const handleLoadMore = () => {
    setItemsToShow(prev => prev + 9);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setItemsToShow(9); // Reset pagination
  };

  const isActiveFilter = (filter) => selectedFilter === filter;

  return (
    <section className="prt-row pricing-plan-section bg-base-grey clearfix">
      <div className="container">
        {/* Título principal */}
        <div className="row">
          <div className="col-xl-12">
            <div className="tm-scrollintetx-wrapper Fristsss">
              <div className="big-title">NUESTROS</div>
            </div>
            <div className="section-title res-991-mb-0">
              <div className="title-header">
                <h2 className="title">
                  <span className="title-heading-underline">Centros</span>
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="row" style={{ marginTop: '30px', marginBottom: '30px' }}>
          <div className="col-12">
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              justifyContent: 'center',
              padding: '0 15px'
            }}>
              {provinces.map(prov => (
                <button
                  key={prov}
                  onClick={() => handleFilterChange(prov)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '4px',
                    border: isActiveFilter(prov) ? '2px solid #57aa46' : '1px solid #ddd',
                    backgroundColor: isActiveFilter(prov) ? '#57aa46' : '#fff',
                    color: isActiveFilter(prov) ? '#fff' : '#666',
                    fontWeight: isActiveFilter(prov) ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '0.85em',
                    fontFamily: 'inherit'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActiveFilter(prov)) {
                      e.currentTarget.style.borderColor = '#57aa46';
                      e.currentTarget.style.color = '#57aa46';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActiveFilter(prov)) {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.color = '#666';
                    }
                  }}
                >
                  {prov}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid de ubicaciones */}
        <div className="row">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={`skel-${i}`} className="col-lg-4 col-md-6 col-sm-6 mb-3">
                  <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '14px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                    borderLeft: '3px solid #e0e0e0',
                    height: '170px',
                    opacity: 0.6
                  }}>
                    <div style={{ height: '14px', width: '60%', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '12px' }} />
                    <div style={{ height: '10px', width: '40%', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '18px' }} />
                    <div style={{ height: '20px', width: '70%', backgroundColor: '#f5f5f5', borderRadius: '12px', marginBottom: '18px' }} />
                    <div style={{ height: '10px', width: '50%', backgroundColor: '#f0f0f0', borderRadius: '4px' }} />
                  </div>
                </div>
              ))
            : displayedLocations.map((location, index) => (
            <div key={index} className="col-lg-4 col-md-6 col-sm-6 mb-3">
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                borderLeft: '3px solid #57aa46',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(87,170,70,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                {/* Location name with icon and map link */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '6px',
                  width: '100%'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    flex: 1,
                    minWidth: 0
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: '2px' }}>
                      <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#57aa46"/>
                    </svg>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#333',
                      lineHeight: '1.4',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {location.name}<span style={{ fontWeight: '400', color: '#999' }}>, {location.province}</span>
                    </div>
                  </div>
                  {location.link && (
                    <a
                      href={location.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#57aa46',
                        textDecoration: 'none',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                        <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" fill="#57aa46"/>
                      </svg>
                      Ver en mapa
                    </a>
                  )}
                </div>

                {/* Contact person - full width */}
                {location.contact && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '10px',
                    width: '100%'
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#999"/>
                    </svg>
                    <span style={{ fontSize: '0.7rem', color: '#555' }}>{location.contact}</span>
                  </div>
                )}

                {/* Service badges - full width */}
                <div style={{
                  marginBottom: '10px',
                  display: 'flex',
                  gap: '6px',
                  flexWrap: 'wrap',
                  width: '100%',
                  justifyContent: 'flex-start'
                }}>
                  {location.services.includes('Psicología') && (
                    <span style={{
                      fontSize: '0.7rem',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(87, 170, 70, 0.1)',
                      color: '#57aa46',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      flex: '1 1 auto'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C10.5 2 9.17 2.62 8.21 3.58C7.26 2.62 5.92 2 4.42 2C2.54 2 1 3.54 1 5.42C1 6.92 1.62 8.26 2.58 9.21L12 18.63L21.42 9.21C22.38 8.26 23 6.92 23 5.42C23 3.54 21.46 2 19.58 2C18.08 2 16.74 2.62 15.79 3.58C14.83 2.62 13.5 2 12 2Z" fill="#57aa46"/>
                      </svg>
                      Psicología
                    </span>
                  )}
                  {location.services.includes('Naturopatía Alimentaria') && (
                    <span style={{
                      fontSize: '0.7rem',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(87, 170, 70, 0.08)',
                      color: '#57aa46',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      flex: '1 1 auto'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.12 2.12C17.12 2.12 9.53 3.97 7.44 9.66C5.92 13.84 7.1 17.39 8.59 19.53C8.59 19.53 6.5 16 6.5 12.5C6.5 9.97 7.65 7.29 9.15 5.29C11.15 2.62 14.12 1.12 17.12 2.12ZM6.71 18.71C5.45 17.45 4.71 15.78 4.71 14C4.71 10.69 7.19 7.71 10.29 6.71C10.29 6.71 8 9.5 8 13C8 15.17 9.09 17.36 10.71 19C12 20.3 13.62 21 15.29 21C18.17 21 20.71 18.93 21.29 16C21.29 16 20.42 19.16 17.71 20.71C15.42 22 12.5 22 10.29 21.29C8.76 20.77 7.66 19.66 6.71 18.71Z" fill="#57aa46"/>
                      </svg>
                      Naturopatía Alimentaria
                    </span>
                  )}
                </div>

                {/* Divider */}
                <div style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#f0f0f0',
                  marginBottom: '10px'
                }}></div>

                {/* Contact info - full width, spread out */}
                <div style={{
                  fontSize: '0.75rem',
                  color: '#666',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  width: '100%',
                  marginTop: 'auto'
                }}>
                  {location.phone && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                        <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="#57aa46"/>
                      </svg>
                      <a
                        href={`tel:${location.phone.replace(/\s/g, '')}`}
                        style={{
                          color: '#57aa46',
                          textDecoration: 'none',
                          fontWeight: '500',
                          flex: 1
                        }}
                      >
                        {location.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="row" style={{ marginTop: '30px' }}>
            <div className="col-12" style={{ textAlign: 'center' }}>
              <button
                onClick={handleLoadMore}
                style={{
                  padding: '12px 35px',
                  border: '2px solid #57aa46',
                  backgroundColor: '#fff',
                  color: '#57aa46',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.95em',
                  transition: 'all 0.2s ease',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#57aa46';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = '#57aa46';
                }}
              >
                Cargar más centros ({filteredLocations.length - itemsToShow})
              </button>
            </div>
          </div>
        )}

        {/* No results message */}
        {!isLoading && filteredLocations.length === 0 && (
          <div className="row">
            <div className="col-12" style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#999', fontSize: '1.1em' }}>
                No se encontraron centros con este filtro
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OfficeLocationsCentersSection;
